// The generic cards' pure logic: reading the config the job holds and checking answers before sending them. The engine checks them
// again (ns-lambda-ts `_core/job/generic/`), so these messages are guidance, not the gate

export type FieldKind = 'text' | 'longText' | 'number' | 'date' | 'yesNo' | 'choice' | 'multiChoice';

export interface FormField {
  key: string;
  kind: FieldKind;
  label: string;
  help?: string;
  required?: boolean;
  options?: { key: string; label: string }[];
  showIf?: { all?: ShowIfCondition[]; any?: ShowIfCondition[] };
}
export interface FormSection {
  kind: 'section';
  label: string;
}
export type FormItem = FormField | FormSection;
export interface ShowIfCondition {
  field: string;
  op: string;
  right?: unknown;
}
export type Answers = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
export const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

// The job's own copy of its node's config, which the engine resolved and wrote onto the job
export function configOf(job: { inputValues?: unknown } | undefined): Record<string, unknown> {
  const inputs = job?.inputValues;
  return isRecord(inputs) && isRecord(inputs.CONFIG) ? inputs.CONFIG : {};
}

export const isSection = (item: FormItem): item is FormSection => item.kind === 'section';

export function formItems(config: Record<string, unknown>): FormItem[] {
  // The engine checked the questions where the node's config was written
  return Array.isArray(config.fields) ? (config.fields.filter(isRecord) as unknown as FormItem[]) : [];
}

const asDate = (value: unknown) => (typeof value === 'string' ? Date.parse(value) : NaN);

// The same typed comparisons as the engine's condition/rule: no coercion, and a missing value fails all but isEmpty
export function holds({ op, right }: ShowIfCondition, left: unknown): boolean {
  if (op === 'isEmpty') return isEmpty(left);
  if (op === 'isSet') return !isEmpty(left);
  if (left === null || left === undefined) return false;
  if (op === 'eq') return left === right;
  if (op === 'ne') return left !== right;
  if (['gt', 'lt', 'gte', 'lte'].includes(op)) {
    if (typeof left !== 'number' || typeof right !== 'number') return false;
    return op === 'gt' ? left > right : op === 'lt' ? left < right : op === 'gte' ? left >= right : left <= right;
  }
  if (op === 'before' || op === 'after') {
    const [l, r] = [asDate(left), asDate(right)];
    if (Number.isNaN(l) || Number.isNaN(r)) return false;
    return op === 'before' ? l < r : l > r;
  }
  if (op === 'in') return Array.isArray(right) && right.includes(left);
  if (op === 'contains') return Array.isArray(left) && left.includes(right);
  return false;
}

export function shows(field: FormField, answers: Answers) {
  if (!field.showIf) return true;
  const conditions = field.showIf.all ?? field.showIf.any ?? [];
  const passes = (condition: ShowIfCondition) => holds(condition, answers[condition.field] ?? null);
  return field.showIf.all ? conditions.every(passes) : conditions.some(passes);
}

// What the form shows for these answers: sections stay, a field shows when its show-if holds against what the engine will
// be sent, the answers of earlier fields that are themselves shown, so a hidden field's leftover answer counts for nothing
export function visibleItems(items: FormItem[], answers: Answers): FormItem[] {
  const sent: Answers = {};
  return items.filter((item) => {
    if (isSection(item)) return true;
    if (!shows(item, sent)) return false;
    if (!isEmpty(answers[item.key])) sent[item.key] = answers[item.key];
    return true;
  });
}

// What goes to the engine: the shown fields' answers, nothing for a field left blank or hidden
export function answersToSend(items: FormItem[], answers: Answers): Answers {
  return Object.fromEntries(
    visibleItems(items, answers)
      .filter((item): item is FormField => !isSection(item))
      .filter((field) => !isEmpty(answers[field.key]))
      .map((field) => [field.key, answers[field.key]]),
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// A message per shown field that the engine would refuse
export function formErrors(items: FormItem[], answers: Answers): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const item of visibleItems(items, answers)) {
    if (isSection(item)) continue;
    const value = answers[item.key];
    if (isEmpty(value)) {
      if (item.required) errors[item.key] = 'This needs an answer.';
    } else if (item.kind === 'number' && (typeof value !== 'number' || !Number.isFinite(value))) {
      errors[item.key] = 'This needs a number.';
    } else if (item.kind === 'date' && (typeof value !== 'string' || !ISO_DATE.test(value))) {
      errors[item.key] = 'This needs a date.';
    } else if (
      (item.kind === 'text' || item.kind === 'longText') &&
      String(value).length > (item.kind === 'text' ? 2000 : 10000)
    ) {
      errors[item.key] = 'This answer is too long.';
    }
  }
  return errors;
}

// An approval's summary row as text: a list (several people's answers) joined, yes or no for a boolean, a dash for nothing
export function summaryText(value: unknown): string {
  if (isEmpty(value)) return '—';
  if (Array.isArray(value)) return value.map(summaryText).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function summaryRows(config: Record<string, unknown>) {
  const rows = Array.isArray(config.summary) ? config.summary.filter(isRecord) : [];
  return rows.map((row) => ({ label: String(row.label ?? ''), value: summaryText(row.value) }));
}

export const commentNeeded = (config: Record<string, unknown>, outcome: 'approve' | 'reject') =>
  outcome === 'reject' && config.commentOnReject === true;

export interface ChecklistItem {
  key: string;
  label: string;
}

export function checklistItems(config: Record<string, unknown>): ChecklistItem[] {
  const items = Array.isArray(config.items) ? config.items.filter(isRecord) : [];
  return items.map((item) => ({ key: String(item.key ?? ''), label: String(item.label ?? '') }));
}

// What stops a checklist being done: an item left when every one is needed, or the statement not confirmed
export function checklistErrors(config: Record<string, unknown>, checked: string[], confirmed: boolean): string[] {
  const errors: string[] = [];
  const items = checklistItems(config);
  if (config.allRequired === true && items.some((item) => !checked.includes(item.key)))
    errors.push('Tick every item first.');
  if (typeof config.confirm === 'string' && config.confirm && !confirmed) errors.push('Confirm the statement first.');
  return errors;
}
